import axios from "axios";

/**
 * Telegram Service - Frontend
 * Gửi thông báo trực tiếp đến Telegram từ frontend
 *
 * ⚠️ CẢNH BÁO BẢO MẬT:
 * - Bot token được expose trong frontend code
 * - Chỉ nên dùng cho development/testing
 * - Với production, nên dùng backend API để bảo mật tốt hơn
 */

class TelegramService {
  constructor() {
    // Lấy cấu hình từ environment variables
    this.telegramApiUrl = process.env.REACT_APP_TELEGRAM_API_URL;
    this.chatId = process.env.REACT_APP_TELEGRAM_CHAT_ID;
  }

  /**
   * Kiểm tra xem Telegram có được cấu hình không
   */
  isConfigured() {
    return !!(this.telegramApiUrl && this.chatId);
  }

  /**
   * Gửi tin nhắn text đến Telegram
   * @param {string} text - Nội dung tin nhắn (hỗ trợ HTML)
   * @param {object} options - Các tùy chọn bổ sung
   */
  async sendMessage(text, options = {}) {
    if (!this.isConfigured()) {
      console.warn("Telegram chưa được cấu hình. Bỏ qua gửi tin nhắn.");
      return { success: false, error: "Not configured" };
    }

    try {
      const payload = {
        chat_id: this.chatId,
        text: text,
        parse_mode: options.parse_mode || "HTML",
        disable_notification: options.disable_notification || false,
        ...options,
      };

      const response = await axios.post(this.telegramApiUrl, payload, {
        timeout: 10000, // 10 seconds timeout
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("Telegram message sent successfully:", response.data);
      return { success: true, data: response.data };
    } catch (error) {
      console.error(
        "Error sending Telegram message:",
        error.response?.data || error.message
      );
      return {
        success: false,
        error: error.response?.data?.description || error.message,
      };
    }
  }

  /**
   * Gửi thông báo feedback mới
   * @param {object} feedbackData - Dữ liệu feedback
   */
  async sendFeedbackNotification(feedbackData) {
    const { userId, userName, userEmail, content, rating } = feedbackData;

    // Format rating as stars
    const ratingStars = rating > 0 ? "⭐".repeat(rating) : "Không đánh giá";

    // Format message với HTML
    const message = `
🔔 <b>PHẢN HỒI MỚI TỪ NGƯỜI DÙNG</b>

📧 <b>Email:</b> ${userEmail || "N/A"}
🆔 <b>User ID:</b> ${userId || "N/A"}

⭐ <b>Đánh giá:</b> ${ratingStars}

💬 <b>Nội dung:</b>
${this.escapeHtml(content)}

🔗 <b>URL:</b> ${window.location.href}
🕐 <b>Thời gian:</b> ${new Date().toLocaleString("vi-VN")}
    `.trim();

    return await this.sendMessage(message);
  }

  /**
   * Escape HTML để tránh lỗi parse_mode
   * @param {string} text - Text cần escape
   */
  escapeHtml(text) {
    if (!text) return "";
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  /**
   * Gửi tin nhắn đơn giản
   * @param {string} message - Nội dung tin nhắn
   */
  async sendSimpleMessage(message) {
    return await this.sendMessage(message, { parse_mode: "HTML" });
  }
}

// Export singleton instance
const telegramService = new TelegramService();
export default telegramService;
